import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import LandingPage from "@/pages/LandingPage";
import SundayBuilder from "@/pages/SundayBuilder";
import AccessSuccess from "@/pages/AccessSuccess";
import MemberLogin from "@/pages/MemberLogin";
import Enter from "@/pages/Enter";
import ParishIntelligence from "@/pages/ParishIntelligence";
import ParishProfile from "@/pages/ParishProfile";
import WorshipPlanner from "@/pages/WorshipPlanner";
import AnnualPlanner from "@/pages/AnnualPlanner";
import GuestMinistryPlanner from "@/pages/GuestMinistryPlanner";
import DossierDemo from "@/pages/DossierDemo";
import ChoralIntelligenceDemo from "@/pages/ChoralIntelligenceDemo";
import ChoralEditorialDemo from "@/pages/ChoralEditorialDemo";
import AdminChoralWorkspace from "@/pages/AdminChoralWorkspace";
import ReportHistory from "@/pages/ReportHistory";
import ReportDetail from "@/pages/ReportDetail";
import SharedReport from "@/pages/SharedReport";
import NotificationCenter from "@/pages/NotificationCenter";
import ParishMemory from "@/pages/ParishMemory";
import Dashboard from "@/pages/Dashboard";
import Onboarding from "@/pages/Onboarding";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Account from "@/pages/Account";
import BillingSuccess from "@/pages/BillingSuccess";
import Diocese from "@/pages/Diocese";
import Legal from "@/pages/Legal";
import AuthCallback from "@/pages/AuthCallback";
import FoundingCircle from "@/pages/FoundingCircle";
import TheAsk from "@/pages/TheAsk";
import AdminFoundingCircle from "@/pages/AdminFoundingCircle";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminAnnualPlanningQA from "@/pages/AdminAnnualPlanningQA";
import AdminRepertoire from "@/pages/AdminRepertoire";
import AdminMusicalIntelligencePreview from "@/pages/AdminMusicalIntelligencePreview";
import AdminMusicalIntelligenceShadow from "@/pages/AdminMusicalIntelligenceShadow";
import AdminMusicalIntelligenceCanary from "@/pages/AdminMusicalIntelligenceCanary";
import AdminDenominatorDrift from "@/pages/AdminDenominatorDrift";
import AdminCatalogGaps from "@/pages/AdminCatalogGaps";
import AdminCatholicSourcesWorkspace from "@/pages/AdminCatholicSourcesWorkspace";
import AdminOrdoAiProofEngine from "@/pages/AdminOrdoAiProofEngine";
import MusicalIntelligenceLearnMore from "@/pages/MusicalIntelligenceLearnMore";
import Pricing from "@/pages/Pricing";
import Billing from "@/pages/Billing";
import DataPrivacy from "@/pages/DataPrivacy";
import DeletionCancel from "@/pages/DeletionCancel";
import InstallPrompt from "@/components/InstallPrompt";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { NotificationProvider } from "@/hooks/useNotifications";
import NotificationBell from "@/components/NotificationBell";
import NotificationToaster from "@/components/NotificationToaster";
import "@/print.css";

function WorshipPlannerOrAnnualRedirect() {
  // Legacy links (dashboard cards, LargerArcs, marketing copy) reached
  // the year-scoped workspace via /worship-planner?scope=Annual+Plan
  // before the Annual Planner Phase 2 split. Redirect those in place.
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const scope = (params.get("scope") || "").toLowerCase();
  if (scope === "annual plan" || scope === "annual") {
    return <Navigate to="/annual-planner" replace />;
  }
  return <WorshipPlanner />;
}


function GlobalNotifChrome() {
  const { user } = useAuth();
  const location = useLocation();
  const hiddenOn = ["/", "/login", "/register", "/forgot-password", "/reset-password",
                    "/pricing", "/sundaybuilder", "/access", "/member-login", "/enter",
                    "/privacy", "/terms", "/cookies", "/the-ask", "/diocese"];
  const hidden = hiddenOn.includes(location.pathname) ||
                 location.pathname.startsWith("/shared-report/") ||
                 location.pathname.startsWith("/founding-circle");
  if (!user || hidden) return null;
  return (<><NotificationBell /><NotificationToaster /></>);
}

function AppRouter() {
  const location = useLocation();
  // Handle the Google OAuth return (session_id in URL fragment) before any route renders.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (<>
    <GlobalNotifChrome />
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/musical-intelligence" element={<MusicalIntelligenceLearnMore />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/welcome" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/parish-intelligence" element={<ProtectedRoute><ParishIntelligence /></ProtectedRoute>} />
      <Route path="/parish-profile" element={<ProtectedRoute><ParishProfile /></ProtectedRoute>} />
      <Route path="/worship-planner" element={<ProtectedRoute><WorshipPlannerOrAnnualRedirect /></ProtectedRoute>} />
      <Route path="/annual-planner" element={<ProtectedRoute><AnnualPlanner /></ProtectedRoute>} />
      <Route path="/guest-ministry" element={<ProtectedRoute><GuestMinistryPlanner /></ProtectedRoute>} />
      <Route path="/preview/dossier-demo" element={<DossierDemo />} />
      <Route path="/preview/choral-intelligence" element={<ChoralIntelligenceDemo />} />
      <Route path="/preview/choral-editorial" element={<ChoralEditorialDemo />} />
      <Route path="/admin/musical-intelligence/choral" element={<AdminChoralWorkspace />} />
      <Route path="/reports" element={<ProtectedRoute><ReportHistory /></ProtectedRoute>} />
      <Route path="/reports/:id" element={<ProtectedRoute><ReportDetail /></ProtectedRoute>} />
      <Route path="/shared-report/:token" element={<SharedReport />} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationCenter /></ProtectedRoute>} />
      <Route path="/parish-memory" element={<ProtectedRoute><ParishMemory /></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
      <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
      <Route path="/diocese" element={<Diocese />} />
      <Route path="/privacy" element={<Legal />} />
      <Route path="/terms" element={<Legal />} />
      <Route path="/cookies" element={<Legal />} />
      <Route path="/access" element={<AccessSuccess />} />
      <Route path="/member-login" element={<MemberLogin />} />
      <Route path="/enter" element={<Enter />} />
      <Route path="/sundaybuilder" element={<SundayBuilder />} />
      <Route path="/founding-circle" element={<FoundingCircle />} />
      <Route path="/the-ask" element={<TheAsk />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
      <Route path="/account/data-privacy" element={<ProtectedRoute><DataPrivacy /></ProtectedRoute>} />
      <Route path="/account/delete/cancel" element={<DeletionCancel />} />
      <Route path="/admin/founding-circle" element={<ProtectedRoute><AdminFoundingCircle /></ProtectedRoute>} />
      <Route path="/admin/billing" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/annual-planning-qa" element={<ProtectedRoute><AdminAnnualPlanningQA /></ProtectedRoute>} />
      <Route path="/admin/repertoire" element={<ProtectedRoute><AdminRepertoire /></ProtectedRoute>} />
      <Route path="/admin/musical-intelligence" element={<ProtectedRoute><AdminMusicalIntelligencePreview /></ProtectedRoute>} />
      <Route path="/admin/musical-intelligence/canary" element={<ProtectedRoute><AdminMusicalIntelligenceCanary /></ProtectedRoute>} />
      <Route path="/admin/musical-intelligence/drift" element={<ProtectedRoute><AdminDenominatorDrift /></ProtectedRoute>} />
      <Route path="/admin/musical-intelligence/catalog-gaps" element={<ProtectedRoute><AdminCatalogGaps /></ProtectedRoute>} />
      <Route path="/admin/musical-intelligence/catholic-sources" element={<ProtectedRoute><AdminCatholicSourcesWorkspace /></ProtectedRoute>} />
      <Route path="/admin/intelligence/proof-engine" element={<ProtectedRoute><AdminOrdoAiProofEngine /></ProtectedRoute>} />
    </Routes>
  </>);
}

function NotifWrapper({ children }) {
  const { user } = useAuth();
  return <NotificationProvider authed={!!user}>{children}</NotificationProvider>;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotifWrapper>
          <InstallPrompt />
          <ErrorBoundary>
            <AppRouter />
          </ErrorBoundary>
        </NotifWrapper>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
